import type { ApolloCache, ApolloClient, FetchResult } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';

import type {
  AiGeneratedImageQuery,
  AiGeneratedImageQueryVariables,
  AiGeneratedImagesQuery,
  AiGeneratedImagesQueryVariables,
  UpdateAiGeneratedImageMutation,
} from 'src/shared/graphql/generated';
import {
  AiGeneratedImageDocument,
  AiGeneratedImagesDocument,
} from 'src/shared/graphql/generated';

export const getCachedAiGeneratedImage = (
  client: ApolloClient<any>,
  id: number,
) =>
  client.readQuery<AiGeneratedImageQuery, AiGeneratedImageQueryVariables>({
    query: AiGeneratedImageDocument,
    variables: { id: `${id}` },
  });

/**
 * Factory function to create a cache updater for injecting new favorites into a
 * query's cached results.
 *
 * @param variables AI generated images query variables (these need to match the
 * variables passed to the query you're updating)
 *
 * @returns function to be passed to the `update` option of the
 * `updateAiGeneratedImage` mutation
 */
export const createFavoritesCacheUpdater =
  (variables: Omit<AiGeneratedImagesQueryVariables, 'favorite'>) =>
  (
    cache: ApolloCache<any>,
    mutationResult: Omit<
      FetchResult<
        UpdateAiGeneratedImageMutation,
        Record<string, any>,
        Record<string, any>
      >,
      'context'
    >,
  ) => {
    cache.updateQuery<AiGeneratedImagesQuery, AiGeneratedImagesQueryVariables>(
      {
        query: AiGeneratedImagesDocument,
        variables: {
          ...variables,
          favorite: true,
        },
      },
      prevData => {
        if (!prevData || !mutationResult.data?.updateAiGeneratedImage) {
          return undefined;
        }

        const mutatedImage = mutationResult.data.updateAiGeneratedImage;
        const existingResultIndex =
          prevData.aiGeneratedImages.results.findIndex(
            result => result?.id === mutatedImage.id,
          );

        if (mutatedImage.favorite && existingResultIndex === -1) {
          const nextData = cloneDeep(prevData);
          nextData.aiGeneratedImages.results.push(mutatedImage);
          return nextData;
        }

        if (!mutatedImage.favorite && existingResultIndex > -1) {
          const nextData = cloneDeep(prevData);
          nextData.aiGeneratedImages.results.splice(existingResultIndex, 1);
          return nextData;
        }

        return undefined;
      },
    );
  };
